import {MEDICINE_BOX_MAX} from '../../../../../utility/medicines';

export const canRecordDose = (dosesGiven, dosesHeld) => {
    const given = Number(dosesGiven);

    if (!dosesGiven || Number.isNaN(given)) {
        return false;
    }

    return Number.isInteger(given) && given >= 1 && given <= Number(dosesHeld ?? 0);
};

// Tells the member of staff why Record is off, once they have typed something.
export const doseProblem = (dosesGiven, dosesHeld) => {
    if (dosesGiven === '' || dosesGiven == null) {
        return '';
    }

    const given = Number(dosesGiven);
    if (Number.isNaN(given) || !Number.isInteger(given) || given < 1) {
        return 'Enter a whole number, 1 or more';
    }

    return given > Number(dosesHeld ?? 0) ? `Only ${Number(dosesHeld ?? 0)} left at school` : '';
};

// Maria, 7 October: the button reads Add Box, whether or not the medicine has a box yet.
export const boxButtonLabel = () => 'Add Box';

export const showsGiveADose = (isSchoolStaff, dosesHeld) => Boolean(isSchoolStaff) && Number(dosesHeld ?? 0) > 0;

/**
 * A dose larger than the box running out first is taken from two boxes and stored as one row per
 * box, all with the same time, giver and note. A person gave one dose, so it reads as one line.
 */
export const oneLinePerDose = (dispenses) => {
    const lines = [];

    for (const dispense of dispenses ?? []) {
        const same = lines.find(line =>
            line.medicine === dispense.medicine &&
            (line.studentMedicineId ?? null) === (dispense.studentMedicineId ?? null) &&
            line.givenAt === dispense.givenAt &&
            line.givenBy === dispense.givenBy &&
            (line.note ?? '') === (dispense.note ?? ''));

        if (same) {
            same.dosesGiven += Number(dispense.dosesGiven);
        } else {
            lines.push({...dispense, dosesGiven: Number(dispense.dosesGiven)});
        }
    }

    return lines;
};

/**
 * AD-2648 QA: the amount in a box is a whole number from 1 up to the largest box. Save stays off
 * until it is, and the message names the field.
 */
export const amountProblem = (amount) => {
    const value = Number(amount);

    if (amount === '' || amount == null || Number.isNaN(value) || !Number.isInteger(value) || value < 1) {
        return 'Amount in box must be a whole number, 1 or more';
    }

    return value > MEDICINE_BOX_MAX ? `Amount in box can be at most ${MEDICINE_BOX_MAX}` : '';
};

/**
 * Olena, 8 October 2026 (point 33): the reorder level is blank, or a whole number from 0 up to the
 * largest box. A larger number was refused by the server without a clear message.
 */
export const reorderProblem = (reorderLevel) => {
    if (reorderLevel === '' || reorderLevel == null) {
        return '';
    }

    const value = Number(reorderLevel);

    return Number.isInteger(value) && value >= 0 && value <= MEDICINE_BOX_MAX
        ? ''
        : `The reorder level must be a whole number from 0 to ${MEDICINE_BOX_MAX.toLocaleString('en-GB')}`;
};

// When a box is corrected, Remaining is a whole number from 0 up to the amount in the box.
export const remainingProblem = ({amount, remaining}) => {
    const value = Number(remaining);

    if (remaining === '' || remaining == null || Number.isNaN(value) || !Number.isInteger(value) || value < 0) {
        return 'Remaining must be a whole number, 0 or more';
    }

    return value > Number(amount) ? 'Remaining cannot be more than the amount in the box' : '';
};

/**
 * Every dose given from one box, newest first, and how many that comes to. A dose larger than the box
 * running out first is stored as one row per box, so each box lists only its own part.
 */
export const dosesFromBox = (dispenses, boxId) => {
    const doses = (dispenses ?? [])
        .filter(dispense => dispense.boxId === boxId)
        .sort((a, b) => new Date(b.givenAt) - new Date(a.givenAt));

    return {doses, given: doses.reduce((sum, dose) => sum + Number(dose.dosesGiven ?? 0), 0)};
};

/**
 * The box the next dose comes from: in use, not expired, with some left, expiring first. A box with no
 * expiry date is used last. The server takes doses in the same order.
 */
export const nextBoxId = (boxes) => {
    const usable = (boxes ?? []).filter(box =>
        !box.disposedAt && !box.emptiedAt && !box.isExpired && Number(box.dosesRemaining) > 0);
    usable.sort((a, b) => (a.expiryDate ?? '9999-12-31').localeCompare(b.expiryDate ?? '9999-12-31') || a.id - b.id);

    return usable[0]?.id ?? null;
};
